import { isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useState } from 'react';

import { track } from '~/src/dash/analytics/analytics';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  fetchChannelMappings,
  IChannelMapping,
  IChannelMappingRow,
  IGetChannelMappingsAPIParams,
  IGetChannelMappingsUiParams,
} from '~/src/dash/views/channels/api';
import { ChannelMappings } from '~/src/dash/views/channels/ChannelMappings';
import { IChannelMappingsConfig } from '~/src/dash/views/channels/ChannelMappings/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ChannelMappingsContainer = () => {
  const { session, user } = useBaseStore();
  const cfg = useBaseConfig().Channels.Mappings as IChannelMappingsConfig;
  const [channelMappings, setChannelMappings] = useState<IChannelMappingRow[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [error, setError] = useState<any>(null);
  const [uiParams, setUiParams] = useState<IGetChannelMappingsUiParams>({});
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);

  // update config for state persistence
  const updateCfg = (params: IGetChannelMappingsUiParams) => {
    if (params.orderBy) cfg.defaultState.orderBy = params.orderBy;
    if (params.order) cfg.defaultState.orderDirection = params.order;
    if (params.currPage) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage) cfg.defaultState.rowsPerPage = params.rowsPerPage;
    if (params.searchInput) cfg.defaultState.searchInput = params.searchInput;
    if (params.activeColumnKeys) cfg.defaultState.activeColumnKeys = params.activeColumnKeys;
    if (params.isTableExpanded) cfg.defaultState.isTableExpanded = params.isTableExpanded;
  };

  // format request params for onFetchChannelMappings
  const formatParams = (params: IGetChannelMappingsUiParams): IGetChannelMappingsAPIParams => ({
    idSearch: parseInt(params.searchInput || '', 10) || '',
    nameSearch: params.searchInput,
    orderBy: params.orderBy,
    orderDirection: params.order,
    page: params.currPage,
    pageSize: params.rowsPerPage,
  });

  // fetch a list of super channels
  const onFetchChannelMappings = (params: IGetChannelMappingsUiParams) => {
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    if (!isEqual(uiParams, params)) {
      updateCfg(params);
      setUiParams(params);
    }
    return fetchChannelMappings(session.publisher.ID, formatParams(params))
      .then(({ ChannelMappings, TotalRows }) => {
        setChannelMappings(formatChannelMappings(ChannelMappings));
        setTotalRows(TotalRows);
        setLoadState(LoadingStateHasData);
        const loadTime = moment().unix() - startTime;
        track(
          'Channels - Mappings - List Loaded',
          {
            isSuperUser: hasRoleSuperUser(user.user),
            loadTime,
            numRowsDisplayed: ChannelMappings.length,
            totalRowCount: TotalRows,
          },
          user.user,
        );
      })
      .catch((err) => {
        setError(err);
        setSnackbarMessage('Error retrieving data.');
        setLoadState(LoadingStateNoData);
      });
  };

  // format onFetchChannelMappings' response
  const formatChannelMappings = (channelMappings: IChannelMapping[]): IChannelMappingRow[] => {
    const channelMappingRows: IChannelMappingRow[] = [];
    channelMappings.forEach((mapping: IChannelMapping) => {
      const { ID, SubSites, Name } = mapping;
      const channelMappingRow: IChannelMappingRow = {
        ID,
        Name: checkUserInfoMasked(user.user) ? `demo_channel_${ID}` : Name, // mask channel info if user is a demo account
        SubChannels: SubSites,
      };
      channelMappingRows.push(channelMappingRow);
    });
    return channelMappingRows;
  };

  return (
    <>
      <ChannelMappings
        cfg={cfg}
        updateCfg={updateCfg}
        fetchChannelMappings={onFetchChannelMappings}
        channelMappings={channelMappings}
        totalRows={totalRows}
        loadState={loadState}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
